import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

const CopyrightRegistration = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Basic", price: "$149", features: ["Single Work Registration", "Filing Assistance", "Copyright Office Application", "Email Support"] },
    { name: "Standard", price: "$299", popular: true, features: ["Everything in Basic", "Multiple Works", "Attorney Review", "Priority Processing", "Expedited Filing"] },
    { name: "Premium", price: "$499", features: ["Everything in Standard", "Dedicated Attorney", "International Copyright", "Infringement Monitoring", "Legal Consultation"] }
  ];

  const process = [
    { step: "01", title: "Submit Your Work", description: "Provide details about the creative work you need to copyright. We assess the best protection strategy for your content." },
    { step: "02", title: "Application Preparation", description: "Our team prepares and reviews your copyright application, ensuring all documentation meets US Copyright Office requirements." },
    { step: "03", title: "Filing & Registration", description: "We file your application, track its progress, and deliver your official copyright registration certificate upon approval." }
  ];

  const testimonials = [
    { name: "David Thompson", role: "Product Designer", text: "Quick, professional copyright registration. The team explained every step clearly and filed my application fast." },
    { name: "Lisa Martinez", role: "Content Creator", text: "Protecting my digital content was essential. They made copyright registration simple and affordable." },
    { name: "Robert Kim", role: "Software Developer", text: "Had my software copyrighted efficiently. Professional handling from start to finish." },
    { name: "Anna Foster", role: "Photographer", text: "Copyright registration for my portfolio was seamless. Now my work is legally protected nationwide." },
    { name: "James Wilson", role: "Author", text: "Filed copyrights for multiple manuscripts. Excellent communication and fast turnaround." }
  ];

  const faqs = [
    { question: "What can be copyrighted?", answer: "Original creative works including books, music, software, photographs, artwork, videos, website content, and any other original expression fixed in a tangible medium." },
    { question: "How long does copyright registration take?", answer: "Standard processing takes 3-6 months. We offer expedited filing options for urgent copyright protection needs." },
    { question: "Is copyright registration mandatory?", answer: "Registration isn't required for copyright to exist, but it provides crucial legal benefits including the ability to sue for infringement and recover statutory damages." },
    { question: "How long does copyright protection last?", answer: "For works created after 1978, copyright lasts for the author's lifetime plus 70 years. For corporate works, it lasts 95 years from publication or 120 years from creation." }
  ];

  return (
    <div className="min-h-screen bg-wine-950">
      <Header onOpenContactForm={() => setIsContactFormOpen(true)} />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-wine-900/50 to-wine-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center text-gold-400 hover:text-gold-500 mb-6 text-sm font-medium transition-colors"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home</Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">US Copyright Registration</h1>
          <p className="text-lg text-wine-300 max-w-2xl mx-auto">Protect your creative works with professional copyright registration and filing services.</p>
        </div>
      </section>

      <section className="py-20 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden h-80 shadow-lg">
              <img src="https://images.unsplash.com/photo-1593444285553-28163240e3f1?w=800&h=600&fit=crop" alt="Copyright claim typewriter" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-wine-500 mb-3">About This Service</p>
              <h2 className="text-3xl font-bold text-wine-950 mb-4">What Is Copyright Registration?</h2>
              <p className="text-stone-600 leading-relaxed mb-4">Copyright registration protects your original creative works, from written content and software to photographs and digital media. Registration with the US Copyright Office establishes a public record of your ownership.</p>
              <p className="text-stone-600 leading-relaxed mb-6">Our copyright attorneys handle the entire filing process, ensuring your creative assets receive full legal protection and giving you the right to pursue infringement claims in federal court.</p>
              <Button onClick={() => setIsContactFormOpen(true)} className="bg-wine-600 hover:bg-wine-700 text-white transition-colors">File My Copyright</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-wine-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item) => (
              <div key={item.step} className="bg-wine-950 p-8 rounded-xl border border-wine-800/40 hover:border-gold-500/30 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="w-10 h-10 bg-wine-600 rounded-lg flex items-center justify-center text-white font-bold text-sm mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-wine-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-wine-950 text-center mb-12">Copyright Filing Packages</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`relative bg-white p-8 rounded-xl border-2 ${plan.popular ? 'border-wine-600 shadow-xl shadow-wine-600/10' : 'border-stone-200'} hover:-translate-y-1 hover:shadow-xl hover:border-gold-400 transition-all duration-300`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-wine-600 text-white px-3 py-1 rounded-full text-xs font-bold">MOST POPULAR</span></div>}
                <h3 className="text-xl font-bold text-wine-950 mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-wine-950 mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">{plan.features.map((f, i) => (<li key={i} className="flex items-start"><Check className="w-4 h-4 text-wine-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-sm text-stone-600">{f}</span></li>))}</ul>
                <Button onClick={() => setIsContactFormOpen(true)} className={`w-full ${plan.popular ? 'bg-wine-600 hover:bg-wine-700 text-white' : 'bg-stone-100 hover:bg-gold-500 hover:text-wine-950 text-stone-800'} transition-colors`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-wine-900 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3">Don't Wait</p>
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Protect Your Creative Works?</h2>
          <p className="text-wine-200 mb-8">Our copyright attorneys are ready to file your application and secure your intellectual property.</p>
          <Button onClick={() => setIsContactFormOpen(true)} className="bg-gold-500 hover:bg-gold-400 text-wine-950 font-semibold px-8 py-5 text-base transition-colors">Start Copyright Registration <ArrowRight className="ml-2 w-4 h-4" /></Button>
        </div>
      </section>

      <ServiceTestimonials testimonials={testimonials} serviceName="Copyright Registration" />

      <section className="py-20" style={{ background: 'linear-gradient(180deg, #1A0A0E 0%, #0D0507 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3 text-center">FAQ</p>
          <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-wine-900/50 rounded-xl px-6 border border-wine-800/40 hover:border-gold-400/30 transition-colors data-[state=open]:border-gold-400/40">
                <AccordionTrigger className="text-left text-base font-medium text-wine-100 hover:text-gold-400 py-5 [&[data-state=open]]:text-gold-400">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-wine-300 text-sm leading-relaxed pb-5">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer onOpenContactForm={() => setIsContactFormOpen(true)} />
      <ContactFormDialog isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </div>
  );
};

export default CopyrightRegistration;
